/**
 * QueueDisplay — the PUBLIC display-board screen (route `queue.display`,
 * param b=<boardId>): the full-screen "now serving" view an admin puts on a
 * lobby TV, independent of Studio. Content is the board's ordered block list
 * (composed in QueueBoardBuilder; legacy boards without blocks render the
 * layout-derived defaults); themes + queuer-display format from the board
 * config; polls getPublicDisplay every 5s.
 */
import { useWfResource } from "../workforce/useWorkforce";
import { useNav } from "../../shell/router";
import { getPublicDisplay, type PublicDisplay } from "./queues-api";
import { BoardQueueCard, boardColors, defaultBoardBlocks } from "./board-blocks";

import { useT } from "@v0y4ge/i18n-runtime";
export default function QueueDisplay() {
  const t = useT();
  const nav = useNav();
  const boardId = (nav.params?.b ?? "") as string;
  const boardToken = (nav.params?.t ?? "") as string;
  const { data, loading, error } = useWfResource<PublicDisplay | null>(
    `qdisplay|${boardId}|${boardToken}`,
    () => (boardId ? getPublicDisplay(boardId, boardToken) : Promise.resolve(null)),
    { pollMs: 5_000 });

  const dark = data?.board.theme !== "light";
  const C = boardColors(dark);

  const frame = (children: React.ReactNode) => (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.fg, fontFamily: C.font, display: "flex", flexDirection: "column", padding: "36px 44px 28px", boxSizing: "border-box" }}>
      {children}
    </div>
  );
  if (!boardId) return frame(<div style={{ margin: "auto", fontSize: 20, color: C.soft }}>{t('queues.display.text')}</div>);
  if (error) return frame(<div style={{ margin: "auto", fontSize: 20, color: C.soft }}>{t('queues.display.text2')}{" "}{error}</div>);
  if (loading && !data) return frame(<div style={{ margin: "auto", fontSize: 20, color: C.soft }}>{t('queues.display.text3')}</div>);
  const d = data!;
  const blocks = d.board.blocks?.length ? d.board.blocks : defaultBoardBlocks(d.board.layout);
  const nameFormat = d.board.nameFormat ?? "first-initial";

  return frame(
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 28 }}>
        <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.3 }}>{d.org}</span>
        <span style={{ fontSize: 15, color: C.soft }}>{d.board.name}</span>
        <span style={{ marginLeft: "auto", fontSize: 14, color: C.soft }}>{new Date(d.asOf).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
      </div>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(${Math.max(1, Math.min(3, d.queues.length))}, minmax(0, 1fr))`, gap: 24, alignItems: "stretch" }}>
        {d.queues.map((q) => (
          <BoardQueueCard key={q.id} q={q} blocks={blocks} C={C} dark={dark} nameFormat={nameFormat} showWait={d.board.showWaitEstimates} />
        ))}
      </div>
      <div style={{ marginTop: 26, textAlign: "center", fontSize: 17, color: C.soft }}>{d.board.message}</div>
    </>
  );
}
